import Component from "@glimmer/component";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { LinkTo } from "@ember/routing";
import Box from "glesys-controlpanel/components/box";
import Copyable from "glesys-controlpanel/components/copyable";
import Table from "glesys-controlpanel/components/table";
import TableTd from "glesys-controlpanel/components/table/td";
import { t } from "ember-intl";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import GButton from "glesys-controlpanel/components/g-button";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import { restartableTask, task, timeout } from "ember-concurrency";
import { not } from "ember-truth-helpers";
import ENV from "glesys-controlpanel/config/environment";
import Cost from "glesys-controlpanel/components/cost";
import IconsLoadingSpinner from "glesys-controlpanel/components/icons/loading-spinner";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntroDetails from "glesys-controlpanel/components/page-content/service-intro-details";
import dataCenterNiceName from "glesys-controlpanel/helpers/data-center-nice-name";
import StatusIndicator from "glesys-controlpanel/components/status-indicator";
import StatusSpinner from "glesys-controlpanel/components/icons/status-spinner";
import IconsMap from "glesys-controlpanel/components/icons/map";
import { findRecord, saveRecord } from "@warp-drive/legacy/compat/builders";

export default class BlockStorageVolumesDetails extends Component {
  @service modal;
  @service http;
  @service router;
  @service intl;
  @service store;
  @service abilities;

  @tracked cost;

  constructor() {
    super(...arguments);

    this.fetchEstimatedCostTask.perform();
    this.pollStatusLoop.perform(this.volume);
  }

  get project() {
    return this.args.model.project;
  }

  get volume() {
    return this.args.model.volume;
  }

  get servers() {
    return this.args.model.servers;
  }

  get dataCenters() {
    return this.args.model.dataCenters;
  }

  get attachmentRows() {
    const { serverid } = this.volume.attachment;
    const server = this.servers?.find((server) => server.id === serverid);

    return [{ key: this.intl.t("label.attached"), serverid, serverName: server?.hostname }];
  }

  renameTask = task(async (newName) => {
    this.volume.name = newName;

    try {
      await this.store.request(saveRecord(this.volume));
    } catch (error) {
      this.volume.rollbackAttributes();

      throw error;
    }
  });

  resizeTask = task(async (size) => {
    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/blockstorage/resize`,
      method: "POST",
      body: {
        volumeid: this.volume.id,
        sizeingib: size,
      },
    });

    this.volume.pushUpdating(response.content.volume);

    this.fetchEstimatedCostTask.perform();
    this.restartPolling();
  });

  attachTask = task(async (serverid) => {
    await this.volume.attachTask.perform(serverid);
    this.restartPolling();
  });

  restartPolling = () => {
    this.pollStatusLoop.perform(this.volume);
  };

  /* istanbul ignore next */
  pollStatusLoop = restartableTask(async (volume) => {
    if (ENV.environment === "test") {
      return;
    }

    while (true) {
      await timeout(volume.isReady ? 60 * 1000 : 4 * 1000);
      await this.store.request(findRecord("block-storage-volume", volume.id, { reload: true }));
    }
  });

  deleteTask = task(async () => {
    await this.volume.deleteTask.perform();

    this.router.transitionTo("block-storage.volumes.overview");
  });

  fetchEstimatedCostTask = restartableTask(async () => {
    if (this.abilities.cannot("access pricing for organization", this.project.organization)) {
      this.cost = { current: { price: null, total: null }, currency: null };
      return;
    }

    const response = await this.http.request({
      url: `${ENV.apiBaseUri}/blockstorage/estimatedcost`,
      method: "POST",
      body: { projectkey: this.project.id, volumeid: this.volume.id },
    });

    this.cost = response.content.billing;
  });

  openModal = (modalName) => {
    const modals = {
      rename: {
        path: "modals/block-storage-rename",
        params: { volume: this.volume, task: this.renameTask },
      },
      resize: this.volume.attachment
        ? {
            path: "modals/block-storage-resize",
            params: { volume: this.volume, task: this.resizeTask },
          }
        : {
            path: "modals/info",
            params: {
              titleText: this.intl.t("block-storage-volumes.details.modal.resize"),
              text: this.intl.t("block-storage-volumes.details.modal.resize-detached-info"),
            },
          },
      attach: {
        path: "modals/block-storage-attach",
        params: { servers: this.servers, task: this.attachTask },
      },
      delete: this.volume.attachment
        ? {
            path: "modals/info",
            params: {
              titleText: this.intl.t("block-storage-volumes.details.modal.delete-attached-title"),
              text: this.intl.t("block-storage-volumes.details.modal.delete-attached-info"),
            },
          }
        : {
            path: "modals/organism-delete",
            params: { entity: this.volume.name, task: this.deleteTask, entityKey: this.volume.id },
          },
    };

    const { path, params } = modals[modalName];

    this.modal.open(path, params);
  };

  <template>
    <PageContent>
      <PageContentServiceIntroDetails class="border-none" @title={{this.volume.name}} @key={{this.volume.id}}>
        {{#if (canManage)}}
          <Dropdown data-test-drop-down-settings-list>
            <:header>
              {{t "block-storage-volumes.details.actions"}}
            </:header>
            <:default>
              <DropdownItem
                data-test-drop-down-list-rename
                disabled={{not this.volume.isReady}}
                {{on "click" (fn this.openModal "rename")}}
              >
                {{t "block-storage-volumes.details.modal.rename"}}
              </DropdownItem>
              <DropdownItem
                data-test-drop-down-list-resize
                disabled={{not this.volume.isReady}}
                {{on "click" (fn this.openModal "resize")}}
              >
                {{t "block-storage-volumes.details.modal.resize"}}
              </DropdownItem>
              <DropdownDestructiveItem
                class="border-t border-gray-200 text-sm flex items-center"
                data-test-organism-delete
                disabled={{not this.volume.isReady}}
                {{on "click" (fn this.openModal "delete")}}
              >
                {{t "button.delete"}}
              </DropdownDestructiveItem>
            </:default>
          </Dropdown>
        {{/if}}
      </PageContentServiceIntroDetails>
      <div
        class="flex flex-wrap items-center gap-4 text-black -mt-10 pb-5 border-b border-gray-200"
        data-test-block-storage-details-meta
      >
        <StatusIndicator @status={{this.volume.statusIndicator}} @showLabel={{true}} />
        <div class="flex items-center gap-1.5" data-test-block-storage-details-data-center>
          <IconsMap class="size-6 stroke-gray-400 stroke-[1.5] -mr-1" />
          {{dataCenterNiceName this.volume.dataCenter this.dataCenters}}
        </div>
      </div>
      <div class="grid grid-cols-3 grid-flow-col gap-8 mt-8">
        <Box data-test-block-storage-details-name>
          <span class="text-gray-500 text-sm flex items-center">
            {{t "label.tier"}}
          </span>
          <p class="text-2xl text-black mt-2 flex flex-wrap">
            {{this.volume.tier.name}}
          </p>
        </Box>
        <Box data-test-block-storage-details-usage>
          <span class="text-gray-500 text-sm flex items-center">
            {{t "label.size"}}
          </span>
          <p class="text-2xl text-black mt-2 flex flex-wrap">
            {{this.volume.sizeInGib}}
            GiB
          </p>
        </Box>
        <Box data-test-block-storage-details-cost>
          <span class="text-gray-500 text-sm flex items-center">
            {{t "label.cost"}}
          </span>
          <div class="text-2xl text-black mt-2 flex items-center gap-2">
            {{#if this.fetchEstimatedCostTask.isRunning}}
              <IconsLoadingSpinner class="size-7" />
            {{else if this.cost}}
              <Cost
                @currency={{this.cost.currency}}
                @price={{this.cost.current.price}}
                @discountedPrice={{this.cost.current.total}}
              />
            {{/if}}
          </div>
        </Box>
      </div>
      <Box class="mt-8" data-test-block-storage-attachment @showFooter={{(canManage)}}>
        <:header>{{t "block-storage-volumes.details.attachment.title"}}</:header>
        <:default>
          {{#unless this.volume.attachment}}
            <p class="text-gray-500 text-base">{{t "block-storage-volumes.details.attachment.description"}}</p>
          {{/unless}}
          <p class="text-gray-500 text-base">{{t "block-storage-volumes.details.attachment.wwn"}}</p>
          {{#if this.volume.wwn}}
            <Copyable
              class="self-start inline-flex mt-2 px-2 py-1 rounded border border-gray-200 bg-gray-50 text-sm text-black"
              data-test-block-storage-details-wwn-value
              @alwaysShowCopyIcon={{true}}
              @contentsWrapperStyle="font-mono"
            >{{this.volume.wwn}}</Copyable>
          {{else}}
            <StatusSpinner class="mt-2" data-test-block-storage-details-wwn-spinner />
          {{/if}}
          {{#if this.volume.attachment}}
            <Table class="-mx-6 -mb-6 mt-8" @items={{this.attachmentRows}} @footer={{false}} @outsideBorders={{false}}>
              <:body as |item|>
                <TableTd class="w-1/6 text-gray-600">{{item.key}}</TableTd>
                <TableTd class="w-5/6">
                  <span class="text-black">{{item.serverName}}</span>
                  <LinkTo
                    class="text-gray-600 underline"
                    data-test-block-storage-volume-attachment-link
                    @route="server.details"
                    @model={{item.serverid}}
                  >
                    ({{item.serverid}})
                  </LinkTo>
                </TableTd>
              </:body>
            </Table>
          {{/if}}
        </:default>
        <:footer>
          {{#if this.volume.attachment}}
            <GButton
              data-test-block-storage-detach
              @color="red"
              @disabled={{not this.volume.isReady}}
              {{on
                "click"
                (modal
                  "modals/confirm"
                  text=(t "block-storage-volumes.details.modal.detach-info")
                  titleText=(t "block-storage-volumes.details.modal.detach-title")
                  buttonText=(t "block-storage-volumes.details.modal.detach-button")
                  task=this.volume.detachTask
                )
              }}
            >
              {{t "block-storage-volumes.details.detach-button"}}
            </GButton>
          {{else}}
            <GButton
              data-test-block-storage-attach
              @color="blue"
              @disabled={{not this.volume.isReady}}
              {{on "click" (fn this.openModal "attach")}}
            >
              {{t "block-storage-volumes.details.attach-button"}}
            </GButton>
          {{/if}}
        </:footer>
      </Box>
    </PageContent>
  </template>
}
